import { GradientOverlay } from '@components/GradientOverlay/GradientOverlay';
import { AudioSelectedIcon } from '@components/Icons/AudioSelectedIcon';
import { SelectableCard } from '@components/SelectableCard/SelectableCard';
import { SelectionCheckbox } from '@components/SelectionCheckbox/SelectionCheckbox';
import { SoundToggle } from '@components/SoundToggle/SoundToggle';
import { TopOverlay } from '@components/TopOverlay/TopOverlay';
import { PlaceholderGradient } from '@components/util/PlaceholderGradient';
import { VideoPreview } from '@components/VideoPreview/VideoPreview';
import { isCompactCell } from '@components/VirtualMasonry/masonryConstants';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { generateIdFromString } from '@lib/utils/colorUtils';
import { getMediaUrl } from '@lib/utils/url';
import { getMediaDuration, isProbablyImage } from '@pages/Library/utils/mediaStatus';
import * as Sentry from '@sentry/react';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import { useUIPreferences } from '@stores/UIPreferences';
import { Media } from '@typings/Media';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useMomentModal } from '../../../context/MomentModalContext';
import {
  canOpenMediaModal,
  isCompleted as isCompletedUtil,
  isProcessingWithVideo as isProcessingWithVideoUtil,
} from '../../../utils/mediaStatus';
import { MediaFolderLocation } from '../../Folders/MediaFolderLocation';
import { useDesktopHoverPlay } from '../hooks/useDesktopHoverPlay';
import { useLibraryCardVideo } from '../hooks/useLibraryCardVideo';
import { MediaCardInfo } from './MediaCardInfo';
import { MediaVerbRail } from './MediaVerbRail';
import css from './VideoCard.module.scss';
import { VideoMenuButton } from './VideoMenuButton';

interface VideoCardProps {
  media: Media;
  src: string;
  libraryId: string;
  mediaId: string;
  thumbnail?: string;
  thumbnailToken?: string | null;
  isAudio?: boolean;
  cellWidth?: number;
  options?: {
    showLowerLeft?: boolean;
    showLowerRight?: boolean;
    showDuration?: boolean;
    showLowerBar?: boolean;
  };
}

export const VideoCard = observer(function VideoCard({
  media,
  src,
  libraryId,
  mediaId,
  thumbnail,
  thumbnailToken,
  isAudio,
  cellWidth,
  options,
}: VideoCardProps) {
  const libraryManager = useLibraryManager();
  const folder = useLibraryFolder();
  const videoSelection = folder.videoSelection;
  const { isOpen, clearError } = useMomentModal();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const uiPreferences = useUIPreferences();
  const isCompleted = isCompletedUtil(media);
  const isProcessingWithVideo = isProcessingWithVideoUtil(media);
  const canOpenModal = canOpenMediaModal(media);
  const isSelected = videoSelection.isSelected(mediaId);

  const { cardRef, videoRef, isPlaybackTarget, isInBand, isCenteredCardActive, pauseVideo } =
    useLibraryCardVideo({
      mediaId,
      autoplayEnabled: uiPreferences.autoplayVideos,
    });

  // Hover owns the reveal wherever the centre line doesn't. No `hasHover` term needed: inside a
  // provider this is exactly that, and where there is no provider (the debug grid) the hook ignores
  // any pointer that isn't a mouse anyway, so enabling it on a touch device produces nothing.
  const { isHovered, isFadingOut, collapseOverlays, hoverHandlers } = useDesktopHoverPlay({
    enabled: !isCenteredCardActive,
    suspended: isOpen,
  });

  const handleClick = () => {
    if (videoSelection.isSelecting) {
      videoSelection.toggleVideoSelection(mediaId);
      trackingStore.mediaSelectionChanged({ totalSelected: videoSelection.selectedCount });
    } else {
      if (canOpenModal) {
        try {
          // Navigate to the media URL - modal will open automatically
          trackingStore.mediaViewed({ mediaId, libraryId });
          navigate(
            getMediaUrl(libraryId, mediaId, {
              libraryName: library?.name,
              mediaName: media.metadata?.name,
            }),
          );
        } catch (err) {
          console.error('Failed to navigate to media:', err);

          // Send error to Sentry with context
          Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
            tags: {
              component: 'VideoCard',
              action: 'navigate_to_media',
            },
            extra: {
              libraryId,
              mediaId,
              mediaName: media.metadata?.filename || 'unknown',
              mediaStatus: media.status,
            },
          });

          // Clear any error state and try again
          clearError();
        }
      }
    }
  };

  const library = libraryManager.libraryById(libraryId);
  const isModifiable = !library?.isReadOnly;
  const isCompact = isCompactCell(cellWidth);

  // The whole row reveals on touch, not one card: a tap is already spoken for (it opens the media)
  // and the neighbours have no hover to fall back on.
  //
  // `isSelected` sits outside that choice because it isn't a hover substitute — it's an independent
  // reason to show the cluster. Folded into the hover branch it meant a selected card that scrolled
  // out of the band stopped rendering its own ticked checkbox.
  const revealedByPointer = isCenteredCardActive ? isInBand : isHovered || isFadingOut;
  const showOverlays = revealedByPointer || isSelected;

  return (
    <SelectableCard
      ref={cardRef}
      className={classNames(css.root, isInBand && css.active)}
      isSelected={isSelected}
      liftOnHover
      {...hoverHandlers}
      onClick={handleClick}
      style={{ cursor: canOpenModal ? 'pointer' : 'default' }}
    >
      <div className={css.videoContainer}>
        {/* Intentional: pass the raw backend duration to VideoPreview even for
            images (frameCount=1). This is a playback range, not a display value,
            so the ~0.03s "play one frame" behavior is the desired result for
            images. The display-side duration suppression lives elsewhere; do
            not gate this with isProbablyImage. */}
        <VideoPreview
          ref={videoRef}
          className={css.videoContainer}
          src={src}
          start={0}
          duration={media.metadata?.video?.duration ?? media.userMetadata?.duration}
          thumbnail={thumbnail || undefined}
          thumbnailToken={thumbnailToken}
          autoPlay={uiPreferences.autoplayVideos}
          loadDelay={isMobile ? 400 : 0}
          externallyPlaying={isPlaybackTarget && uiPreferences.autoplayVideos}
          showThumbnailWhenPaused
          maxMaxBufferLength={3}
          libraryId={libraryId}
        />
      </div>
      {isAudio && (
        <div className={css.audioBackground}>
          <PlaceholderGradient id={generateIdFromString(media.uniqueId || media._id)} blur={200} />
          <AudioSelectedIcon className={css.audioIconOverlay} />
        </div>
      )}
      {showOverlays && (isCompleted || isProcessingWithVideo) && (
        <>
          <div className={classNames(css.overlays, isFadingOut && !isSelected && css.fadeOut)}>
            <TopOverlay
              leftChildren={
                <div className={css.iconButton} onClick={(e) => e.stopPropagation()}>
                  <SoundToggle />
                </div>
              }
              rightChildren={
                <>
                  {/* The agent sparkle used to live here, beside the checkbox and
                      the kebab. It moved down to the verb rail: this cluster is
                      selection + housekeeping, and the verbs read as neither. */}
                  <div className={css.iconButton} onClick={(e) => e.stopPropagation()}>
                    <SelectionCheckbox
                      selection={videoSelection}
                      itemId={mediaId}
                      toggle={(id) => {
                        videoSelection.toggleVideoSelection(id);
                        trackingStore.mediaSelectionChanged({
                          totalSelected: videoSelection.selectedCount,
                        });
                      }}
                    />
                  </div>
                  <div className={css.iconButton} onClick={(e) => e.stopPropagation()}>
                    <VideoMenuButton
                      media={media}
                      isModifiable={isModifiable}
                      onMenuOpened={pauseVideo}
                      onAfterDestructiveAction={collapseOverlays}
                      hasVerbRail
                    />
                  </div>
                </>
              }
            />
            {/* Bottom-aligned just above the title — the same position as the
                library card rail, so one glance teaches both. Not card-centre:
                that's the card's own open target and where the video starts
                playing on the very hover that reveals this.
                Inside `.overlays` (not a sibling of it) so it shares the card's
                fade-in and fade-out rather than popping; that wrapper is
                pointer-events: none, which VerbRail re-enables for its buttons.
                `isCompact` shifts it clear of the two-line title the narrow cell
                layout produces; the duration chip is bottom-right and the rail
                bottom-left, so they never meet.

                `MediaVerbRail` owns the items, the gating and the second
                `useMediaMenuActions` instance this card used to hold itself — all three
                rails share it now, so a change to how a rail derives from `actions` can't
                land on one and miss the others. */}
            <MediaVerbRail
              media={media}
              control="media-card-rail"
              variant="overlay"
              className={classNames(css.verbRail, isCompact && css.verbRailCompact)}
            />
          </div>
        </>
      )}
      <div className={css.gradientOverlay}>
        <div className={css.gradientOverlayInner}></div>
      </div>
      <GradientOverlay variant="bottom-blur" backdropBlur={20} height="110px" zIndex={3} />
      {options?.showLowerBar !== false && (
        <div className={classNames(css.overlays, css.lowerOverlays)}>
          <MediaCardInfo
            title={{
              value: media.metadata?.name ?? '-',
              origin: media.origin,
              show: true,
            }}
            duration={{
              // getMediaDuration treats backend duration=0 (unprocessed sentinel) as
              // unknown and falls through; MediaCardInfo hides the chip on undefined.
              // Still images (frameCount=1) don't have a meaningful duration to show.
              value: isProbablyImage(media) ? undefined : getMediaDuration(media),
              show: options?.showDuration !== false,
            }}
            commentCount={media.commentCount}
            isCompact={isCompact}
            location={<MediaFolderLocation media={media} />}
          />
        </div>
      )}
    </SelectableCard>
  );
});
